import { useState } from 'react';
import { Animated, Pressable, Text, View } from 'react-native';
import { Image } from 'expo-image';
import { getAssetUrl, ASSET_PATHS } from '../../constants/assets';
import {
  ONBOARDING_DUEL,
  ONBOARDING_RIVAL_AVATAR,
  ONBOARDING_YOU_AVATAR,
} from '../../src/tutorial/constants/onboardingContent.constants';
import { EXPO_IMAGE_ALT_DECORATIVE } from '../../src/shared/constants/expoImageAlt.constants';
import { useTutorialEntrance } from '../../src/tutorial/hooks/useTutorialEntrance';
import { OnboardingHeadline } from './OnboardingHeadline';
import type { OnboardingSceneProps } from './onboardingScene.types';
import { rewardArtStyles as styles } from './RewardArt.styles';

// "home" faces right, "away" faces left — so You (left) faces the rival and the rival
// (right) faces You: the two ready avatars look at each other across the VS badge.
const YOU_URI = getAssetUrl(ASSET_PATHS.AVATARS[ONBOARDING_YOU_AVATAR].home);
const RIVAL_URI = getAssetUrl(ASSET_PATHS.AVATARS[ONBOARDING_RIVAL_AVATAR].away);

/** Screen 2 — Duel: head-to-head VS card + interactive pick-type chips. */
export function RewardArt({ isActive, layout, headline, subheadline }: OnboardingSceneProps) {
  const [selected, setSelected] = useState<string>(ONBOARDING_DUEL.defaultPickTypeId);
  const card = useTutorialEntrance(isActive, 120);
  const avatarBox = { width: layout.vsAvatar, height: layout.vsAvatar };

  return (
    <View style={styles.scene}>
      <OnboardingHeadline
        isActive={isActive}
        title={headline}
        subtitle={subheadline}
        highlight="head-to-head."
      />

      <Animated.View
        style={[styles.vsCard, { opacity: card.opacity, transform: [{ scale: card.scale }] }]}
      >
        <View style={styles.vsRow}>
          <View style={styles.vsCol}>
            <View style={[styles.vsAvatarWrap, avatarBox]}>
              <Image
                source={{ uri: YOU_URI }}
                style={styles.vsAvatarImg}
                contentFit="contain"
                alt={EXPO_IMAGE_ALT_DECORATIVE}
              />
            </View>
            <View style={[styles.vsPill, styles.vsPillYou]}>
              <Text style={styles.vsPillYouText}>{ONBOARDING_DUEL.youLabel}</Text>
            </View>
          </View>

          <View style={styles.vsBadge}>
            <Text style={styles.vsBadgeText}>VS</Text>
          </View>

          <View style={styles.vsCol}>
            <View style={[styles.vsAvatarWrap, avatarBox]}>
              <Image
                source={{ uri: RIVAL_URI }}
                style={styles.vsAvatarImg}
                contentFit="contain"
                alt={EXPO_IMAGE_ALT_DECORATIVE}
              />
            </View>
            <View style={[styles.vsPill, styles.vsPillRival]}>
              <Text style={styles.vsPillRivalText} numberOfLines={1}>
                {ONBOARDING_DUEL.rivalName}
              </Text>
            </View>
          </View>
        </View>
        <Text style={styles.vsTagline}>{ONBOARDING_DUEL.vsTagline}</Text>
      </Animated.View>

      <View style={styles.chipsBlock}>
        <Text style={styles.chipsHeader}>{ONBOARDING_DUEL.chipsHeader}</Text>
        <View style={styles.chipsWrap}>
          {ONBOARDING_DUEL.pickTypes.map((pt) => {
            const on = pt.id === selected;
            return (
              <Pressable
                key={pt.id}
                onPress={() => setSelected(pt.id)}
                accessibilityRole="button"
                accessibilityState={{ selected: on }}
                testID={`onboarding-pick-chip-${pt.id}`}
                style={({ pressed }) => [
                  styles.chip,
                  on ? styles.chipOn : styles.chipOff,
                  pressed && styles.chipPressed,
                ]}
              >
                <Text style={[styles.chipText, on ? styles.chipTextOn : styles.chipTextOff]}>
                  {pt.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      <Text style={styles.footer}>{ONBOARDING_DUEL.footer}</Text>
    </View>
  );
}
